import React, { useState, useEffect } from 'react';
import { Receipt, ChevronLeft, ChevronRight, Heart, Sparkles, TrendingUp } from 'lucide-react';
import { getMonthlyReceipt, getReceiptsHistory } from '../services/api';
import { Button } from './ui/button';

export function MonthlyReceipt() {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchReceipt();
    fetchHistory();
  }, [currentMonth]);

  const fetchReceipt = async () => {
    try {
      setLoading(true);
      const res = await getMonthlyReceipt(currentMonth);
      setReceipt(res.data);
    } catch (error) {
      console.error('Error fetching receipt:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await getReceiptsHistory(12);
      setHistory(res.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const navigateMonth = (direction) => {
    if (!receipt) return;
    
    const [year, month] = receipt.month_code.split('-').map(Number);
    let newYear = year;
    let newMonth = month + direction;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setCurrentMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  if (loading) {
    return (
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 animate-pulse">
        <div className="h-8 w-48 bg-[var(--border)] rounded mb-4" />
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-[var(--border)] rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-8 text-center">
        <Receipt size={48} className="mx-auto text-[var(--text-secondary)] mb-4" />
        <h3 className="text-xl font-teko mb-2">No Receipt Available</h3>
        <p className="text-[var(--text-secondary)]">
          Start reading to support your favorite creators!
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden" data-testid="monthly-receipt">
      {/* Header */}
      <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] p-6 text-white">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => navigateMonth(-1)} className="p-1 hover:bg-white/20 rounded">
            <ChevronLeft size={20} />
          </button>
          <h2 className="text-2xl font-teko">{receipt.month}</h2>
          <button onClick={() => navigateMonth(1)} className="p-1 hover:bg-white/20 rounded">
            <ChevronRight size={20} />
          </button>
        </div>
        <p className="text-center text-white/80 text-sm">
          Your Monthly Support Receipt
        </p>
      </div>

      {/* Total */}
      <div className="p-6 border-b border-[var(--border)] text-center">
        <p className="text-sm text-[var(--text-secondary)] mb-1">Total Contribution</p>
        <p className="text-4xl font-teko text-[var(--primary)]">
          ${receipt.total_contribution.toFixed(2)}
        </p>
      </div>

      {/* Allocations */}
      <div className="p-6">
        <h3 className="text-lg font-teko mb-4 flex items-center gap-2">
          <Heart size={18} className="text-[var(--primary)]" />
          Where Your Support Went
        </h3>

        {receipt.allocations.length === 0 ? (
          <p className="text-center text-[var(--text-secondary)] py-4">
            No reading activity this month
          </p>
        ) : (
          <div className="space-y-4">
            {receipt.allocations.map((alloc, index) => (
              <div key={alloc.creator_id} className="flex items-center gap-4">
                {/* Rank */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  index === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                  index === 1 ? 'bg-gray-400/20 text-gray-400' :
                  index === 2 ? 'bg-orange-600/20 text-orange-600' :
                  'bg-[var(--background)] text-[var(--text-secondary)]'
                }`}>
                  {index + 1}
                </div>

                {/* Creator Info */}
                <div className="w-10 h-10 rounded-full bg-[var(--background)] overflow-hidden flex-shrink-0">
                  {alloc.creator_picture ? (
                    <img src={alloc.creator_picture} alt={alloc.creator_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[var(--text-secondary)]">
                      {alloc.creator_name[0]}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{alloc.creator_name}</p>
                  <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                    <span>{alloc.engagement.pages_read} pages</span>
                    <span>•</span>
                    <span>{alloc.engagement.likes} likes</span>
                    {alloc.floored && (
                      <span className="text-green-500">(min protected)</span>
                    )}
                    {alloc.capped && (
                      <span className="text-blue-500">(fair capped)</span>
                    )}
                  </div>
                </div>

                {/* Amount */}
                <div className="text-right">
                  <p className="font-bold text-lg">${alloc.amount.toFixed(2)}</p>
                  <p className="text-xs text-[var(--text-secondary)]">{alloc.percentage}%</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Undiscovered Boost */}
        {receipt.undiscovered_boost?.enabled && (
          <div className="mt-6 p-4 bg-[var(--primary)]/10 rounded-lg border border-[var(--primary)]/20">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} className="text-[var(--primary)]" />
              <span className="font-medium text-[var(--primary)]">Undiscovered Creator Boost</span>
            </div>
            <p className="text-sm text-[var(--text-secondary)]">
              ${receipt.undiscovered_boost.amount.toFixed(2)} went to support new and emerging creators
            </p>
          </div>
        )}
      </div>

      {/* Impact Summary */}
      <div className="p-6 bg-[var(--background)] border-t border-[var(--border)]">
        <p className="text-center text-[var(--text-secondary)] mb-2">
          {receipt.impact_summary}
        </p>
        <p className="text-center text-sm italic text-[var(--primary)]">
          "{receipt.philosophy}"
        </p>
      </div>

      {/* Visual Bar Chart */}
      {receipt.allocations.length > 0 && (
        <div className="p-6 border-t border-[var(--border)]">
          <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
            <TrendingUp size={14} />
            Distribution Breakdown
          </h4>
          <div className="h-4 rounded-full overflow-hidden flex bg-[var(--background)]">
            {receipt.allocations.map((alloc, i) => (
              <div
                key={alloc.creator_id}
                style={{ width: `${alloc.percentage}%` }}
                className={`h-full ${
                  i === 0 ? 'bg-[var(--primary)]' :
                  i === 1 ? 'bg-[var(--secondary)]' :
                  i === 2 ? 'bg-purple-500' :
                  i === 3 ? 'bg-blue-500' :
                  'bg-gray-500'
                }`}
                title={`${alloc.creator_name}: ${alloc.percentage}%`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {receipt.allocations.slice(0, 5).map((alloc, i) => (
              <div key={alloc.creator_id} className="flex items-center gap-1 text-xs">
                <div className={`w-2 h-2 rounded-full ${
                  i === 0 ? 'bg-[var(--primary)]' :
                  i === 1 ? 'bg-[var(--secondary)]' :
                  i === 2 ? 'bg-purple-500' :
                  i === 3 ? 'bg-blue-500' :
                  'bg-gray-500'
                }`} />
                <span className="text-[var(--text-secondary)]">{alloc.creator_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default MonthlyReceipt;
